import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  Button,
  Skeleton,
} from "@mui/material";
import title from "global/constants/TitleConstants";
import { useTitle } from "utils/UseTitle";
import ManageMFAModal from "./ManageMFAModal";
import { useEffect, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { fetchProfileDetails } from "../SettingsService";
import { handleErrorMessage } from "helpers/methods";
import Viewcode from "./ViewCode";
import Passkeys from "./Passkeys";

const Security = () => {
  useTitle(title.SECURITY);
  const [manageMFA, setManageMFA] = useState<boolean>(false);
  const [viewQRCode, setViewQRCode] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<any>({});
  const [loader, setLoader] = useState<boolean>(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoader(true);
      const response = await fetchProfileDetails();
      setUserProfile(response);
      setLoader(false);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    }
  };

  const getMFAQRCodeModal = () => {
    return (
      <ManageMFAModal
        visible={manageMFA}
        handleDialogClose={() => setManageMFA(false)}
        updateMFAStatus={(status: boolean) =>
          setUserProfile({
            ...userProfile,
            multiFactorLogin: status,
          })
        }
        mfaEnabled={userProfile.multiFactorLogin ?? false}
      />
    );
  };

  const getMFASection = () => {
    return (
      <Card sx={{ mt: 2 }}>
        <CardHeader title="Multi Factor Authentication" />
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            {loader ? (
              <Skeleton variant="circular" width={24} height={24} />
            ) : userProfile.multiFactorLogin ? (
              <CheckCircleIcon fontSize="small" htmlColor="#85C935" />
            ) : (
              <CancelIcon fontSize="small" htmlColor="#f04438" />
            )}
            {loader ? (
              <Skeleton variant="text" width={100} />
            ) : (
              <Typography
                color={userProfile.multiFactorLogin ? "#85C935" : "#f04438"}
                variant="body1"
              >
                {userProfile.multiFactorLogin ? "Enabled" : "Not Configured"}
              </Typography>
            )}
          </Stack>
          {loader ? (
            <>
              <Skeleton variant="text" width={150} sx={{ mt: 2 }} />
              <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
            </>
          ) : (
            <>
              <Typography sx={{ mt: 1 }} variant="subtitle2">
                Authenticator App
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                Use an authenticator app to generate one-time security codes.
              </Typography>
            </>
          )}
          <Stack direction="row" spacing={1} mt={4}>
            {loader ? (
              <>
                <Skeleton variant="rectangular" width={80} height={32} />
                <Skeleton variant="rectangular" width={100} height={32} />
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setManageMFA(true)}
                  size="small"
                >
                  {userProfile.multiFactorLogin ? "Disable" : "Set Up"}
                </Button>
                {userProfile.multiFactorLogin && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => setViewQRCode(true)}
                    size="small"
                  >
                    View QR Code
                  </Button>
                )}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      <Typography variant="h5" color="text.primary">
        Security
      </Typography>
      {getMFASection()}
      <Passkeys />
      {getMFAQRCodeModal()}
      <Viewcode
        visible={viewQRCode}
        handleDialogClose={() => setViewQRCode(false)}
      />
    </CenteredAuthenticatedLayout>
  );
};

export default Security;
